import { AnalyticsService } from '@alison/analytics/client';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { FilterCheckbox } from '../../../filters/FilterCheckbox.js';

interface TrendMetricPickerProps {
  availableMetrics: string[];
  metricNames: [string] | [string, string];
  onMetricNamesChange: (metricNames: [string] | [string, string]) => void;
  label?: string;
  metricLabel?: (name: string) => string;
}

export function toggleMetric(
  metricNames: [string] | [string, string],
  name: string,
): [string] | [string, string] | null {
  if (metricNames.includes(name)) {
    if (metricNames.length === 1) return null;
    const remaining = metricNames.find((existing) => existing !== name);
    return remaining === undefined ? null : [remaining];
  }
  if (metricNames.length === 2) return null;
  return [metricNames[0], name];
}

export function TrendMetricPicker({
  availableMetrics,
  metricNames,
  onMetricNamesChange,
  label = 'Metrics',
  metricLabel = (name) => name.toUpperCase(),
}: TrendMetricPickerProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          height: 28,
          px: 2,
          border: '1px solid',
          borderColor: theme.palette.border.strong,
          borderRadius: `${theme.radius.md}px`,
          bgcolor: 'background.paper',
          fontFamily: 'inherit',
          cursor: 'pointer',
        })}
      >
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          {label}
        </Box>
        <Box component="span" sx={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
          {metricNames.map(metricLabel).join(' + ')}
        </Box>
        <ArrowDropDownIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </Box>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: 1,
              width: anchor?.offsetWidth,
              p: 0.5,
              border: '1px solid',
              borderColor: theme.palette.border.default,
              boxShadow: theme.shadows[5],
            }),
          },
        }}
      >
        {availableMetrics.map((name) => {
          const checked = metricNames.includes(name);
          const atLimit = !checked && metricNames.length === 2;
          return (
            <FormControlLabel
              key={name}
              control={
                <Checkbox
                  disableRipple
                  checked={checked}
                  disabled={atLimit}
                  onChange={() => {
                    const next = toggleMetric(metricNames, name);
                    if (next !== null) {
                      AnalyticsService.track('Creative Detail - Change Trend Metric', {
                        metric: name,
                      });
                      onMetricNamesChange(next);
                    }
                  }}
                  icon={<FilterCheckbox checked={false} />}
                  checkedIcon={<FilterCheckbox checked />}
                  sx={{ p: 0 }}
                />
              }
              label={
                <Box component="span" sx={{ flex: 1, fontSize: 12, fontWeight: 600 }}>
                  {metricLabel(name)}
                </Box>
              }
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                m: 0,
                py: 1,
                px: 1.5,
                borderRadius: `${theme.radius.md}px`,
                transition: theme.transitions.create('background-color', {
                  duration: theme.transitions.duration.shortest,
                }),
                '&:hover': { bgcolor: 'background.page' },
              })}
            />
          );
        })}
      </Popover>
    </>
  );
}
